import { api, ApiError, type SoundCloudSyncJob, type SoundCloudImportTrack, type SoundCloudImportMedia } from "./api";
import { soundcloudFetch, soundcloudJson } from "./soundcloud-browser";
import type { SoundCloudLibrary } from "../types";

const bootstrapIds = ["Pb72ranhoyt6gw7hM7TkzUItXlMWSNSo"];
const profileUrl = "https://soundcloud.com/hvrpvk";
const maxAudioBytes = 200 * 1024 * 1024;
type RemoteTrack = { id: number; kind: string; title: string; duration: number; permalink_url: string; genre?: string; description?: string; artwork_url?: string; track_authorization?: string; user?: { username?: string; full_name?: string; avatar_url?: string }; media?: { transcodings?: Transcoding[] } };
type Transcoding = { url: string; format?: { protocol?: string; mime_type?: string }; snipped?: boolean };
type Like = { track?: RemoteTrack; created_at?: string };
type Media = { bytes: Uint8Array; type: SoundCloudImportMedia["type"] };
type Progress = { message: string; completed: number; total: number; fraction?: number };

async function resolveProfile(clientId: string) {
  return soundcloudJson<{ id: number; kind: string }>(`https://api-v2.soundcloud.com/resolve?url=${encodeURIComponent(profileUrl)}&client_id=${clientId}`);
}

export function findSoundCloudClientIds(source: string): string[] {
  return [...new Set([...source.matchAll(/["']?(?:client_id|clientId)["']?\s*[:=]\s*["']([a-zA-Z0-9]{32})["']/g)].map((match) => match[1]!))];
}

async function discoverClient(report: (message: string) => void) {
  const candidates = new Set([localStorage.getItem("nkriz-soundcloud-client-id"), ...bootstrapIds].filter((id): id is string => Boolean(id)));
  report("Connecting to the SoundCloud API…");
  for (const id of candidates) {
    try { const user = await resolveProfile(id); if (user.kind === "user" && user.id) return { clientId: id, user }; }
    catch (error) { if (error instanceof Error && !error.message.includes("HTTP 401") && !error.message.includes("HTTP 403")) throw error; }
  }
  // Public website bundles carry SoundCloud's current web client id. The optional
  // browser helper permits reading these when the website does not allow CORS.
  report("Refreshing SoundCloud’s expired API key from its website…");
  const response = await soundcloudFetch(profileUrl);
  if (!response.ok) throw new Error(`Could not open SoundCloud (HTTP ${response.status})`);
  const html = await response.text();
  const scripts = [...html.matchAll(/<script\b[^>]*\bsrc=["']([^"']+)["']/g)].map((match) => new URL(match[1]!, profileUrl).href).slice(-12).reverse();
  for (const [index, script] of scripts.entries()) {
    report(`Reading SoundCloud API settings (${index + 1}/${scripts.length})…`);
    const scriptResponse = await soundcloudFetch(script);
    if (!scriptResponse.ok) continue;
    const bundle = await scriptResponse.text();
    for (const id of findSoundCloudClientIds(bundle)) {
      if (candidates.has(id)) continue;
      candidates.add(id);
      try { const user = await resolveProfile(id); if (user.kind === "user" && user.id) { localStorage.setItem("nkriz-soundcloud-client-id", id); return { clientId: id, user }; } }
      catch { /* try next public client id */ }
    }
  }
  throw new Error("Could not find SoundCloud’s current web API key. Install the Browser helper and reload, then try again.");
}

export async function findNewLikes(userId: number, clientId: string, knownIds: Set<string>): Promise<Like[]> {
  const newest: Like[] = [];
  const seen = new Set(knownIds);
  const visited = new Set<string>();
  let knownInRow = 0;
  let url: string | null = `https://api-v2.soundcloud.com/users/${userId}/likes?client_id=${clientId}&limit=50&linked_partitioning=1`;
  while (url) {
    if (visited.has(url)) throw new Error("SoundCloud returned a repeated likes page");
    visited.add(url);
    const page: { collection?: Like[]; next_href?: string } = await soundcloudJson(url);
    for (const like of page.collection ?? []) {
      if (!like.track || like.track.kind !== "track" || !like.track.id) continue;
      const id = String(like.track.id);
      if (knownIds.has(id)) { knownInRow++; if (knownInRow >= 5) break; continue; }
      if (seen.has(id)) continue;
      knownInRow = 0;
      seen.add(id);
      newest.push(like);
    }
    if (knownInRow >= 5 || !page.next_href || !page.collection?.length) break;
    const next = new URL(page.next_href);
    if (next.hostname !== "api-v2.soundcloud.com") throw new Error("Unexpected likes page host");
    next.searchParams.set("client_id", clientId);
    url = next.href;
  }
  return newest.reverse();
}

async function binary(url: string): Promise<Uint8Array> {
  const response = await soundcloudFetch(url);
  if (!response.ok) throw new Error(`SoundCloud media returned HTTP ${response.status}`);
  const announced = Number(response.headers.get("Content-Length"));
  if (announced > maxAudioBytes) throw new Error("Track exceeds the 200 MB browser download limit");
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!bytes.length || bytes.length > maxAudioBytes) throw new Error("Invalid media size");
  return bytes;
}

export async function downloadHls(url: string, mime: string, depth = 0, onProgress?: (fraction: number) => void): Promise<Media> {
  if (depth > 3) throw new Error("Invalid HLS playlist nesting");
  const response = await soundcloudFetch(url);
  if (!response.ok) throw new Error(`SoundCloud playlist returned HTTP ${response.status}`);
  const playlist = await response.text();
  if (!playlist.startsWith("#EXTM3U")) throw new Error("Invalid HLS playlist");
  const lines = playlist.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.some((line) => line.startsWith("#EXT-X-STREAM-INF"))) {
    const variant = lines.find((line) => !line.startsWith("#"));
    if (!variant) throw new Error("Empty HLS master playlist");
    return downloadHls(new URL(variant, url).href, mime, depth + 1, onProgress);
  }
  if (lines.some((line) => line.startsWith("#EXT-X-KEY") && !line.includes("METHOD=NONE") && !line.includes("METHOD=AES-128"))) throw new Error("This track uses a protected HLS stream");
  if (lines.some((line) => line.startsWith("#EXT-X-BYTERANGE"))) throw new Error("This track uses an unsupported ranged HLS stream");
  const init = lines.find((line) => line.startsWith("#EXT-X-MAP"))?.match(/URI="([^"]+)"/)?.[1];
  const segments = lines.filter((line) => !line.startsWith("#"));
  if (!segments.length || segments.length > 4000) throw new Error("Invalid HLS segment count");
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const segment of [...(init ? [init] : []), ...segments]) {
    const bytes = await binary(new URL(segment, url).href);
    total += bytes.length;
    if (total > maxAudioBytes) throw new Error("Track exceeds the 200 MB browser download limit");
    parts.push(bytes);
    onProgress?.(parts.length / (segments.length + (init ? 1 : 0)));
  }
  // Standard HLS AES-128 keys are part of the public playable stream. Keys stay
  // in the browser and are never uploaded with the resulting audio.
  if (lines.some((line) => line.startsWith("#EXT-X-KEY") && line.includes("METHOD=AES-128"))) {
    if (init) throw new Error("Encrypted MP4 initialization is unsupported; trying another format");
    const sequence = Number(lines.find((line) => line.startsWith("#EXT-X-MEDIA-SEQUENCE:"))?.split(":")[1] || 0);
    let keyUrl: string | undefined;
    let explicitIv: string | undefined;
    let partIndex = 0;
    const keys = new Map<string, CryptoKey>();
    for (const line of lines) {
      if (line.startsWith("#EXT-X-KEY:")) {
        keyUrl = line.includes("METHOD=NONE") ? undefined : line.match(/URI="([^"]+)"/)?.[1];
        explicitIv = line.match(/(?:,|:)IV=0x([0-9a-fA-F]+)/)?.[1];
      } else if (!line.startsWith("#")) {
        if (keyUrl) {
          const absoluteKey = new URL(keyUrl, url).href;
          let key = keys.get(absoluteKey);
          if (!key) { key = await crypto.subtle.importKey("raw", await binary(absoluteKey) as BufferSource, "AES-CBC", false, ["decrypt"]); keys.set(absoluteKey, key); }
          const iv = new Uint8Array(16);
          if (explicitIv) {
            const hex = explicitIv.padStart(32, "0");
            if (hex.length !== 32) throw new Error("Invalid HLS encryption IV");
            for (let index = 0; index < 16; index++) iv[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
          } else new DataView(iv.buffer).setBigUint64(8, BigInt(sequence + partIndex));
          parts[partIndex] = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-CBC", iv }, key, parts[partIndex]! as BufferSource));
        }
        partIndex++;
      }
    }
    total = parts.reduce((sum, part) => sum + part.length, 0);
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) { bytes.set(part, offset); offset += part.length; }
  // MPEG-TS needs remuxing, unlike raw MP3, ADTS AAC, or fragmented MP4.
  if (bytes[0] === 0x47 && bytes[188] === 0x47) throw new Error("This stream requires remuxing; trying another format");
  const type = init || mime.includes("mp4") ? "audio/mp4" : mime.includes("aac") ? "audio/aac" : "audio/mpeg";
  return { bytes, type };
}

export async function downloadAudio(track: RemoteTrack, clientId: string, onProgress?: (fraction: number) => void): Promise<Media> {
  const supported = (entry: Transcoding) => ["progressive", "hls", "encrypted-hls"].includes(entry.format?.protocol ?? "");
  const candidates = [...(track.media?.transcodings ?? [])].filter((entry) => !entry.snipped && supported(entry));
  candidates.sort((a, b) => Number(a.format?.protocol !== "progressive") - Number(b.format?.protocol !== "progressive"));
  const errors: string[] = [];
  for (const transcoding of candidates) {
    try {
      const auth = new URL(transcoding.url);
      auth.searchParams.set("client_id", clientId);
      if (track.track_authorization) auth.searchParams.set("track_authorization", track.track_authorization);
      const stream = await soundcloudJson<{ url?: string }>(auth.href);
      if (!stream.url) throw new Error("No stream URL");
      const mime = transcoding.format?.mime_type ?? "audio/mpeg";
      const media = transcoding.format?.protocol !== "progressive" ? await downloadHls(stream.url, mime, 0, onProgress) : {
        bytes: await binary(stream.url), type: (mime.includes("mp4") ? "audio/mp4" : "audio/mpeg") as Media["type"]
      };
      if (media.bytes.length < 1000) throw new Error("Audio response is incomplete");
      return media;
    } catch (error) { errors.push(error instanceof Error ? error.message : String(error)); }
  }
  throw new Error(errors.join("; ") || "SoundCloud has no downloadable full stream for this track. Use its SoundCloud playback instead.");
}

async function downloadArtwork(track: RemoteTrack): Promise<Media | undefined> {
  const urls = [track.artwork_url, track.user?.avatar_url].filter((url): url is string => Boolean(url));
  for (const original of urls) {
    for (const url of [...new Set([original.replace(/-(large|t\d+x\d+)\./, "-t500x500."), original])]) {
      try {
        const response = await soundcloudFetch(url);
        const type = response.headers.get("Content-Type")?.split(";")[0];
        if (!response.ok || !["image/jpeg", "image/png", "image/webp"].includes(type ?? "")) continue;
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (bytes.length && bytes.length <= 10 * 1024 * 1024) return { bytes, type: type as Media["type"] };
      } catch { /* try the original size, then the artist avatar */ }
    }
  }
}

function base64(bytes: Uint8Array): string {
  let text = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) text += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(text);
}

export async function uploadMedia(job: SoundCloudSyncJob, clientId: string, trackId: string, kind: "artwork" | "audio", media: Media, onProgress?: (fraction: number) => void): Promise<SoundCloudImportMedia> {
  const chunkSize = 64 * 1024;
  const chunks = Math.ceil(media.bytes.length / chunkSize);
  for (let index = 0; index < chunks; index++) {
    const data = base64(media.bytes.subarray(index * chunkSize, (index + 1) * chunkSize));
    for (let attempt = 0; ; attempt++) {
      try { await api.soundcloudUploadChunk(job.id, trackId, kind, index, { clientId, data }, (loaded, total) => { if (total > 0) onProgress?.((index + loaded / total) / chunks); }); break; }
      catch (error) { if (attempt >= 2) throw error; await new Promise((done) => setTimeout(done, 750 * (attempt + 1))); }
    }
    onProgress?.((index + 1) / chunks);
  }
  return { chunks, bytes: media.bytes.length, type: media.type };
}

export async function syncLikesInBrowser(library: SoundCloudLibrary, onProgress: (progress: Progress) => void): Promise<{ newTracks: number; audioFailures: number; message: string }> {
  // A separate id for each invocation prevents another tab from writing into this job.
  const clientId = crypto.randomUUID();
  let progress: Progress = { message: "Connecting to SoundCloud through your browser…", completed: 0, total: 0 };
  onProgress(progress);
  // Establish connectivity before reserving the server import slot.
  const { clientId: apiClient, user } = await discoverClient((message) => { progress = { ...progress, message }; onProgress(progress); });
  const { job } = await api.soundcloudSync(clientId);
  let heartbeatError: unknown;
  const heartbeat = window.setInterval(() => {
    void api.soundcloudSyncProgress(job.id, { clientId, ...progress }).catch((error) => {
      // Chunk uploads also refresh the job. A transient heartbeat timeout must
      // not abort successfully uploading audio; ownership/auth failures must.
      if (error instanceof ApiError && [401, 409].includes(error.status)) heartbeatError = error;
    });
  }, 15_000);
  let added = 0;
  let audioFailures = 0;
  try {
    progress = { ...progress, message: "Checking your latest likes in Chrome…" }; onProgress(progress);
    const likes = await findNewLikes(user.id, apiClient, new Set(library.tracks.map((track) => track.soundcloudId)));
    // Retry missing audio from earlier partial imports as well as downloading new likes.
    const repairs: Like[] = library.tracks.filter((track) => !track.audio).map((track) => ({ track: { id: Number(track.soundcloudId), kind: "track", title: track.title, duration: track.durationMs, permalink_url: track.permalinkUrl } }));
    const items = [...likes, ...repairs];
    progress.total = items.length;
    for (let index = 0; index < items.length; index++) {
      if (heartbeatError) throw heartbeatError;
      const like = items[index]!;
      const raw = like.track!;
      progress = { message: `Downloading ${index + 1}/${items.length}: ${raw.title}`, completed: index, total: items.length }; onProgress(progress);
      await api.soundcloudSyncProgress(job.id, { clientId, ...progress });
      const track = await soundcloudJson<RemoteTrack>(`https://api-v2.soundcloud.com/tracks/${raw.id}?client_id=${apiClient}`).catch(() => raw);
      const metadata: SoundCloudImportTrack = {
        clientId, soundcloudId: String(track.id), title: track.title || raw.title, artist: track.user?.username || track.user?.full_name || "",
        permalinkUrl: track.permalink_url || raw.permalink_url, durationMs: track.duration || 0, genre: track.genre || "", description: track.description || "", likedAt: like.created_at || null
      };
      const artwork = await downloadArtwork(track);
      let audio: Media | undefined;
      try { audio = await downloadAudio(track, apiClient, (fraction) => { progress = { ...progress, fraction: fraction * .7 }; onProgress(progress); }); }
      catch (error) { metadata.audioError = (error instanceof Error ? error.message : String(error)).slice(0, 500); audioFailures++; }
      progress = { ...progress, fraction: .7, message: `Uploading ${index + 1}/${items.length}: ${raw.title}` }; onProgress(progress);
      await api.soundcloudSyncProgress(job.id, { clientId, ...progress });
      if (artwork) metadata.artwork = await uploadMedia(job, clientId, String(track.id), "artwork", artwork);
      if (audio) metadata.audio = await uploadMedia(job, clientId, String(track.id), "audio", audio, (fraction) => { progress = { ...progress, fraction: .7 + fraction * .29 }; onProgress(progress); });
      const result = await api.soundcloudImportTrack(job.id, metadata);
      if (result.added) added++;
      progress.completed = index + 1;
      progress.fraction = 0;
    }
    const message = added ? `Added ${added} new like${added === 1 ? "" : "s"} from your browser.` : "Likes are up to date.";
    const summary = message + (audioFailures ? ` ${audioFailures} track${audioFailures === 1 ? "" : "s"} could not be downloaded and will be retried next time.` : "");
    await api.soundcloudSyncProgress(job.id, { clientId, ...progress, message: summary, state: "done" });
    return { newTracks: added, audioFailures, message: summary };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Could not check SoundCloud likes";
    await api.soundcloudSyncProgress(job.id, { clientId, ...progress, state: "error", message: message.slice(0, 500) }).catch(() => undefined);
    throw error;
  } finally { clearInterval(heartbeat); }
}
